import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Spinner, Table, Button, Card, Icon } from '../../../../core/ui';
import { CSSTransition } from 'react-transition-group';
import axios from '../../../../core/api/client';
import { useTranslation } from '../../../../core/context/TranslationContext';
import styles from './EditReceiptPage.module.css';

const EditReceiptPage = () => {
  const { receiptId } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editedItems, setEditedItems] = useState({});
  const [show, setShow] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setShow(true);
    const fetchReceiptData = async () => {
      try {
        setLoading(true);
        const [receiptResponse, itemsResponse] = await Promise.all([
          axios.get(`/api/receipts/${receiptId}`),
          axios.get(`/api/receipts/${receiptId}/items`),
        ]);
        setReceipt(receiptResponse.data);
        setItems(itemsResponse.data);
        setEditedItems(itemsResponse.data.reduce((acc, item) => {
          acc[item.id] = { ...item };
          return acc;
        }, {}));
      } catch (err) {
        setError(t('errorFetchingReceipt'));
      } finally {
        setLoading(false);
      }
    };
    fetchReceiptData();
  }, [receiptId, t]);

  const handleItemChange = (itemId, field, value) => {
    setEditedItems(prev => {
      const updatedItem = { ...prev[itemId], [field]: value };
      if (field === 'unitPrice' || field === 'quantity') {
        updatedItem.totalPrice = (updatedItem.quantity || 0) * (updatedItem.unitPrice || 0);
      }
      return { ...prev, [itemId]: updatedItem };
    });
  };

  const handleSaveChanges = async () => {
    try {
      const updatedItems = Object.values(editedItems).map(item => ({
        id: item.id,
        productName: item.productName,
        category: item.category,
        quantity: parseFloat(item.quantity),
        unitPrice: parseFloat(item.unitPrice),
        totalPrice: parseFloat(item.quantity) * parseFloat(item.unitPrice),
        lineNumber: item.lineNumber || 0,
      }));

      await axios.post(`/api/receipts/${receiptId}/items`, updatedItems, {
        headers: { 'Content-Type': 'application/json' },
      });
      navigate(`/receipts?receipt=${receiptId}`);
    } catch (err) {
      setError(t('errorUpdatingReceipt'));
    }
  };

  const handleBack = () => {
    setShow(false);
    const params = new URLSearchParams(window.location.search);
    const groupName = params.get('group');
    const groupQuery = groupName ? `&group=${encodeURIComponent(groupName)}` : '';

    setTimeout(() => {
      navigate(`/receipts?receipt=${receiptId}${groupQuery}`);
    }, 300);
  };

  const formatCurrency = (amount) => {
    if (isNaN(amount)) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('tr-TR');
    } catch (e) {
      console.error('Invalid date format:', dateString);
      return '-';
    }
  };

  if (loading) {
    return (
      <Container className={styles.loadingContainer}>
        <Spinner label={t('loading')} />
      </Container>
    );
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <CSSTransition
      in={show}
      timeout={300}
      classNames={{ 
        enter: styles.pageEnter,
        enterActive: styles.pageEnterActive,
        exit: styles.pageExit,
        exitActive: styles.pageExitActive,
       }}
      unmountOnExit
    >
      <Container className={styles.editPageContainer}>
        <Card>
          <Card.Header>
            <div className={styles.headerRow}>
              <div>
                <h5 className={styles.title}>{receipt.storeName}</h5>
                {receipt && (
                  <span className={styles.dateText}>
                    {formatDate(receipt.purchaseDateTime)}
                  </span>
                )}
              </div>
              <div className={styles.headerActions}>
                <button className={styles.backBtn} onClick={handleBack}>
                  <Icon name="fa-arrow-left" size={15} /> {t('back')}
                </button>
                <Button variant="primary" onClick={handleSaveChanges}>
                  {t('saveChanges')}
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <div className={styles.tableContainer}>
              <Table striped hover className={styles.itemsTable}>
                <thead>
                  <tr>
                    <th>{t('product')}</th>
                    <th>{t('category')}</th>
                    <th>{t('quantity')}</th>
                    <th>{t('unitPrice')}</th>
                    <th>{t('totalPrice')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id}>
                      <td data-label={t('product')}>
                        <input
                          type="text"
                          className={styles.tableInput}
                          value={editedItems[item.id]?.productName || ''}
                          onChange={e => handleItemChange(item.id, 'productName', e.target.value)}
                        />
                      </td>
                      <td data-label={t('category')}>
                        <input
                          type="text"
                          className={styles.tableInput}
                          value={editedItems[item.id]?.category || ''}
                          onChange={e => handleItemChange(item.id, 'category', e.target.value)}
                        />
                      </td>
                      <td data-label={t('quantity')}>
                        <input
                          type="number"
                          step="0.01"
                          className={styles.tableInput}
                          value={editedItems[item.id]?.quantity || ''}
                          onChange={e => handleItemChange(item.id, 'quantity', parseFloat(e.target.value))}
                        />
                      </td>
                      <td data-label={t('unitPrice')}>
                        <input
                          type="number"
                          step="0.01"
                          className={styles.tableInput}
                          value={editedItems[item.id]?.unitPrice || ''}
                          onChange={e => handleItemChange(item.id, 'unitPrice', parseFloat(e.target.value))}
                        />
                      </td>
                      <td data-label={t('totalPrice')}>
                        {formatCurrency(editedItems[item.id]?.totalPrice)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </CSSTransition>
  );
};

export default EditReceiptPage;
